/**
 * My Golf.
 *
 * Every other golf screen in this application answers a question the
 * professional asks. This one answers the question the member asks, which
 * is "can I get out at nine on Saturday, and who is already on it?"
 *
 * Three decisions worth naming, because they are the difference between a
 * member page and a smaller version of the pro shop:
 *
 *   The booking window is shown before it is enforced. A member refused
 *   at eight on Saturday should have been able to read why beforehand;
 *   the alternative is a phone call to the shop that the software caused.
 *
 *   The sheet shows names and not money. At a private club knowing who is
 *   already out at nine is the point of having a sheet — whether their
 *   guest fee has posted is the shop's business, and the API strips it.
 *
 *   Nothing here re-implements a rule. The advance window, the weekend
 *   restriction, guest sponsorship and slot capacity are all the
 *   database's (GOLF-4 to GOLF-8). This page shows what it knows, sends
 *   the booking, and reports the refusal in the words it came back with.
 */

import { useState } from 'react';
import { api } from '../lib/api.ts';
import { useApi, useMutation } from '../lib/useApi.ts';
import { useSession } from '../lib/session.tsx';
import { Card, Empty, ErrorBanner, Loading, Money, Status, Tile } from '../components/ui.tsx';
import { CourseAlertBanner, useCourseAlerts } from '../components/CourseAlert.tsx';
import { CourseConditions } from '../components/CourseConditions.tsx';
import { GolfEvents } from '../components/GolfEvents.tsx';
import { MyResults } from '../components/Leaderboard.tsx';
import { MyStandings } from '../components/Standings.tsx';
import { ContactPreferences } from '../components/ContactPreferences.tsx';
import { date as fmtDate, dateTime, titleCase } from '../lib/format.ts';

const hhmm = (t: string) => t.slice(0, 5);
const today = () => new Date().toISOString().slice(0, 10);

interface MyPayload {
  is_member: boolean;
  privilege: {
    may_book: boolean;
    advance_days: number;
    weekend_earliest_tee_time: string | null;
    weekday_earliest_tee_time: string | null;
    max_guests_per_round: number | null;
    category_name: string;
    books_up_to: string;
  } | null;
  bookings: {
    booking_id: string; course_name: string; play_date: string; starts_at: string;
    status: string; holes: number; notes: string | null;
    players: { name: string; kind: string; tee: string | null; riding: boolean }[];
  }[];
  lessons: {
    lesson_id: string; starts_at: string; duration_minutes: number; kind: string;
    status: string; professional_name: string; amount_minor: number | null;
  }[];
  practice: {
    booking_id: string; facility_name: string; facility_kind: string;
    starts_at: string; duration_minutes: number; party_size: number;
    status: string; amount_minor: number | null;
  }[];
  handicap: {
    ghin_number: string | null; index_value: string | null;
    effective_on: string | null; source: string | null;
  } | null;
  scores: {
    score_id: string; played_on: string; course_name: string; tee_name: string;
    holes: number; gross: number | null; adjusted_gross: number | null;
    net: number | null; differential: string | null; course_handicap: number | null;
    posting_status: string; holes_played: number;
  }[];
  scores_held: number;
}

interface SheetSlot {
  tee_time_id: string;
  starts_at: string;
  max_players: number;
  spaces_left: number;
  slot_status: string;
  event_name: string | null;
  held_until: string | null;
  bookings: { players: { name: string; kind: string }[] }[];
}

/* ------------------------------------------------------------------ */

export function MyGolfPage() {
  const { currency } = useSession();
  const [date, setDate] = useState(today());
  const [note, setNote] = useState<string | null>(null);

  const mine = useApi<MyPayload>('/v1/golf/my');
  // Above everything, including the loading state below — a member
  // opening this page in a storm must see it before the page has
  // finished deciding what else to show them.
  const alerts = useCourseAlerts();
  const sheet = useApi<{ slots: SheetSlot[] }>(
    `/v1/golf/tee-sheet?date=${date}`, [date]);
  const pins = useApi<{ set_count: number; holes: { playing_number: number;
    said_aloud: string | null; difficulty: string | null }[] }>(
    `/v1/golf/pins?date=${today()}`);

  const [booking, setBooking] = useState<SheetSlot | null>(null);
  const [party, setParty] = useState('1');
  const [holes, setHoles] = useState('18');

  const book = useMutation(async (slot: SheetSlot) => {
    // A member books for themselves plus however many guests. The API
    // takes the membership from the login regardless of what is sent, so
    // there is nothing here to get wrong.
    const players = [{ player_kind: 'member' as const }];
    for (let i = 1; i < Number(party); i++) {
      players.push({ player_kind: 'member' as const });
    }
    await api('/v1/golf/bookings', {
      method: 'POST',
      body: { tee_time_id: slot.tee_time_id, holes: Number(holes), players },
    });
    setBooking(null);
    setNote(`Booked ${hhmm(slot.starts_at)} on ${fmtDate(date)}.`);
    mine.reload();
    sheet.reload();
  });

  const cancel = useMutation(async (id: string) => {
    const r = await api<{ late: boolean; hours_notice: number; already: boolean }>(
      `/v1/golf/my/bookings/${id}/cancel`, { method: 'POST', body: {} });
    setNote(
      r.already
        ? 'That one was already given back.'
        : r.late
          ? `Given back with ${r.hours_notice} hours' notice — the shop has been told.`
          : 'Given back.',
    );
    mine.reload();
    sheet.reload();
  });

  const m = mine.data;
  const slots = sheet.data?.slots ?? [];

  if (mine.loading) {
    return (
      <>
        <CourseAlertBanner data={alerts} />
        <Card><Loading /></Card>
      </>
    );
  }

  if (m && !m.is_member) {
    return (
      <>
        {/* The banner belongs here too, and it was missing until a browser
            pass caught it. A professional who opens this page in a storm
            is standing in the same storm as the members are — a screen
            that says "this login holds no tee times" and nothing else is
            a screen that hid the one thing on it that mattered. */}
        <CourseAlertBanner data={alerts} />
        <h1>My Golf</h1>
        <Card>
          <Empty>
            This login is a staff login rather than a member&apos;s, so it holds no tee
            times of its own. The tee sheet is where everybody else&apos;s are.
          </Empty>
        </Card>
      </>
    );
  }

  return (
    <>
      <div className="row" style={{ marginBottom: 16 }}>
        <div>
          <h1>My Golf</h1>
          <div className="muted small">
            What you are holding, and what is still free.
          </div>
        </div>
      </div>

      <CourseAlertBanner data={alerts} />

      {note && <div className="notice">{note}</div>}
      <ErrorBanner error={mine.error ?? cancel.error} />

      {/* What the board decided this category may do, shown rather than
          discovered. A member refused on Saturday morning should have been
          able to read this on Thursday. */}
      {m?.privilege && (
        <Card title={m.privilege.category_name}>
          <div className="tiles">
            <Tile label="You may book" value={`${m.privilege.advance_days} days ahead`}
              note={`up to ${fmtDate(m.privilege.books_up_to)}`} />
            <Tile label="Weekends from"
              value={m.privilege.weekend_earliest_tee_time
                ? hhmm(m.privilege.weekend_earliest_tee_time)
                : 'any time'} />
            <Tile label="Weekdays from"
              value={m.privilege.weekday_earliest_tee_time
                ? hhmm(m.privilege.weekday_earliest_tee_time)
                : 'any time'} />
            {m.privilege.max_guests_per_round !== null && (
              <Tile label="Guests" value={m.privilege.max_guests_per_round}
                note="per round" />
            )}
            {m.handicap?.index_value && (
              <Tile label="Handicap index" value={m.handicap.index_value}
                note={m.handicap.effective_on
                  ? `as at ${fmtDate(m.handicap.effective_on)}` : undefined} />
            )}
          </div>
          {!m.privilege.may_book && (
            <Status tone="warning">
              This category does not book the course. The shop can tell you what it does
              include.
            </Status>
          )}
        </Card>
      )}

      <Card title="What You Are Holding">
        {!m || m.bookings.length === 0 ? (
          <Empty>Nothing booked. The sheet below is where the tee times are.</Empty>
        ) : (
          <table className="data">
            <thead>
              <tr>
                <th>Day</th><th>Time</th><th>Course</th>
                <th>Playing</th><th className="num">Holes</th><th />
              </tr>
            </thead>
            <tbody>
              {m.bookings.map((b) => (
                <tr key={b.booking_id}>
                  <td>{fmtDate(b.play_date)}</td>
                  <td className="mono">{hhmm(b.starts_at)}</td>
                  <td>{b.course_name}</td>
                  <td className="small">
                    {b.players.map((p) => p.name).join(', ') || '—'}
                    {b.notes && <div className="hint">{b.notes}</div>}
                  </td>
                  <td className="num">{b.holes}</td>
                  <td>
                    {b.status === 'booked' && (
                      <button className="secondary small" disabled={cancel.busy}
                        onClick={() => cancel.run(b.booking_id)}>
                        Give it back
                      </button>
                    )}
                    {b.status !== 'booked' && (
                      <span className="muted small">{titleCase(b.status.replace(/_/g, ' '))}</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>

      {/* Before the sheet, because whether the eighth is open changes
          whether somebody wants the eight o'clock. */}
      <CourseConditions />

      {/* Above the sheet too. A member deciding what to do with their
          summer wants the member-guest before they want next Tuesday. */}
      <GolfEvents />

      {/* Above their own rounds, because "where did I finish" is asked
          before "what did I shoot". And the season above the afternoon:
          most members entered Saturday because of the year. */}
      <MyStandings />
      <MyResults />

      {/* Their own record, which is the other half of a handicap. The
          index tile above says what the association thinks; this says
          what this club has actually seen. */}
      <Card
        title="Your Rounds"
        actions={m && m.scores_held > 0
          ? <span className="muted small">{m.scores_held} on file here</span>
          : undefined}
      >
        {!m || m.scores.length === 0 ? (
          <Empty>
            Nothing recorded yet. Cards are entered in the shop after a round — ask
            them to put yours in and it will show here.
          </Empty>
        ) : (
          <>
            <table className="data">
              <thead>
                <tr>
                  <th>Day</th><th>Course</th><th>Tee</th>
                  <th className="num">Score</th><th className="num">Net</th>
                  <th className="num">Diff.</th><th>With the association</th>
                </tr>
              </thead>
              <tbody>
                {m.scores.map((r) => (
                  <tr key={r.score_id}>
                    <td>{fmtDate(r.played_on)}</td>
                    <td className="small">{r.course_name}</td>
                    <td className="small">{r.tee_name}</td>
                    <td className="num">
                      {r.gross ?? '—'}
                      {/* The number that gets posted is not always the
                          number that was shot. Said here rather than
                          discovered in a dispute. */}
                      {r.adjusted_gross !== null && r.gross !== null
                        && r.adjusted_gross !== r.gross && (
                        <div className="hint">posts as {r.adjusted_gross}</div>
                      )}
                      {r.holes === 9 && <div className="hint">nine holes</div>}
                    </td>
                    <td className="num">{r.net ?? '—'}</td>
                    <td className="num mono">{r.differential ?? '—'}</td>
                    <td className="small">
                      {r.posting_status === 'sent' || r.posting_status === 'acknowledged'
                        ? <Status tone="good">Sent</Status>
                        : r.posting_status === 'not_posted'
                          ? <span className="muted">Not posted</span>
                          : <Status tone="warning">Waiting</Status>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            {/* The one thing that stops this page being read as a
                handicap record. It is not one, and cannot be. */}
            <p className="hint" style={{ marginTop: 10 }}>
              These are the rounds this club holds. Your index is set by your
              association from every round you post anywhere — rounds played away
              are not shown here and are not missing.
            </p>
          </>
        )}
      </Card>

      <Card
        title="The Sheet"
        actions={<input type="date" value={date}
          onChange={(e) => setDate(e.target.value)} />}
      >
        <ErrorBanner error={sheet.error ?? book.error} />

        {sheet.loading ? <Loading /> : slots.length === 0 ? (
          <Empty>
            The sheet is not open for {fmtDate(date)} yet. The shop opens it a week at a
            time.
          </Empty>
        ) : (
          <table className="data">
            <thead>
              <tr><th>Time</th><th>Who is out</th><th className="num">Spaces</th><th /></tr>
            </thead>
            <tbody>
              {slots.map((s) => {
                const beingBooked = Boolean(s.held_until)
                  && new Date(s.held_until!) > new Date();
                const owned = s.slot_status === 'event';
                const blocked = s.slot_status === 'blocked';
                const names = s.bookings.flatMap((b) => b.players.map((p) => p.name));
                return (
                  <tr key={s.tee_time_id}
                    style={blocked || owned ? { opacity: 0.55 } : undefined}>
                    <td className="mono">{hhmm(s.starts_at)}</td>
                    <td className="small">
                      {blocked ? <Status tone="serious">Not in play</Status>
                        : owned ? <Status tone="neutral">{s.event_name ?? 'An event'}</Status>
                          : beingBooked && names.length === 0
                            ? <Status tone="warning">Somebody is booking this</Status>
                            : names.length > 0
                              ? names.join(', ')
                              : <span className="muted">—</span>}
                    </td>
                    <td className="num">
                      {blocked || owned ? '—' : `${s.spaces_left} of ${s.max_players}`}
                    </td>
                    <td>
                      {!blocked && !owned && !beingBooked && s.spaces_left > 0 && (
                        <button className="secondary small"
                          onClick={() => { setBooking(s); setParty('1'); setHoles('18'); }}>
                          Book
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </Card>

      {booking && (
        <Card title={`Book ${hhmm(booking.starts_at)} on ${fmtDate(date)}`}>
          <form onSubmit={(e) => { e.preventDefault(); book.run(booking); }}>
            <ErrorBanner error={book.error} />
            <div className="grid-2">
              <label className="field">
                <span>How many of you</span>
                <input required type="number" min={1} max={booking.spaces_left}
                  value={party} onChange={(e) => setParty(e.target.value)} />
                <div className="hint">
                  {booking.spaces_left} of {booking.max_players} still free.
                </div>
              </label>
              <label className="field">
                <span>Holes</span>
                <select value={holes} onChange={(e) => setHoles(e.target.value)}>
                  <option value="18">18</option>
                  <option value="9">9</option>
                </select>
              </label>
            </div>
            {/* Said plainly rather than discovered on submit. Guests need
                a member answerable for them, and the shop is better at
                that conversation than a form is. */}
            <div className="hint" style={{ marginBottom: 10 }}>
              Bringing guests? Ring the shop — a guest needs a member answerable for them,
              and the fee lands on that member&apos;s account.
            </div>
            <div className="actions">
              <button className="gold" disabled={book.busy}>
                {book.busy ? 'Booking…' : 'Book it'}
              </button>
              <button type="button" className="secondary" onClick={() => setBooking(null)}>
                Cancel
              </button>
            </div>
          </form>
        </Card>
      )}

      {(m?.lessons.length ?? 0) > 0 && (
        <Card title="Your Lessons">
          <table className="data">
            <thead>
              <tr><th>When</th><th>With</th><th>Kind</th>
                <th className="num">Minutes</th><th className="num">Fee</th></tr>
            </thead>
            <tbody>
              {m!.lessons.map((l) => (
                <tr key={l.lesson_id}>
                  <td>{dateTime(l.starts_at)}</td>
                  <td>{l.professional_name}</td>
                  <td className="muted small">{titleCase(l.kind)}</td>
                  <td className="num">{l.duration_minutes}</td>
                  <td className="num">
                    {l.amount_minor
                      ? <Money minor={l.amount_minor} currency={currency} />
                      : <span className="muted">—</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      )}

      {(m?.practice.length ?? 0) > 0 && (
        <Card title="The Practice Center">
          <table className="data">
            <thead>
              <tr><th>When</th><th>Where</th><th className="num">Minutes</th>
                <th className="num">Players</th><th className="num">Fee</th></tr>
            </thead>
            <tbody>
              {m!.practice.map((p) => (
                <tr key={p.booking_id}>
                  <td>{dateTime(p.starts_at)}</td>
                  <td>{p.facility_name}</td>
                  <td className="num">{p.duration_minutes}</td>
                  <td className="num">{p.party_size}</td>
                  <td className="num">
                    {p.amount_minor
                      ? <Money minor={p.amount_minor} currency={currency} />
                      : <span className="muted">—</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      )}

      {/* Today's pins, read-only. The thing a member most wants on the
          first tee, and the reason the software holds the sheet at all. */}
      {(pins.data?.set_count ?? 0) > 0 && (
        <Card title="Where the Holes Are Today">
          <div className="bars" style={{ gap: 4 }}>
            {pins.data!.holes.filter((h) => h.said_aloud).map((h) => (
              <div className="bar-row" key={h.playing_number}>
                <div className="bar-row__label">Hole {h.playing_number}</div>
                <div className="bar-row__track" style={{ background: 'transparent' }}>
                  <strong>{h.said_aloud}</strong>
                  {h.difficulty === 'hard' && (
                    <span className="muted small"> · a hard one</span>
                  )}
                </div>
                <div className="bar-row__value" />
              </div>
            ))}
          </div>
        </Card>
      )}

      {/* Last, because it is a settings card on a page about playing —
          but on this page rather than buried in a settings tree, because
          it is the screen somebody looks for the moment a message they
          did not want arrives. */}
      <ContactPreferences />
    </>
  );
}
